import React, { useMemo, useEffect, useRef } from "react";
import { useParams, useHistory, useLocation } from "react-router-dom";
import { TabView, TabPanel } from "primereact/tabview";
import DashboardLayout from "../../layouts/DashboardLayout";
import SettingsPageHeader from "./components/SettingsPageHeader/SettingsPageHeader";
import SettingsSidebar from "./components/SettingsSidebar/SettingsSidebar";
import RegionalPreferences from "./components/RegionalPreferences/RegionalPreferences";
import ProfileSettingsForm from "./components/ProfileSettingsForm/ProfileSettingsForm";
import ContactInfoForm from "./components/ContactInfoForm/ContactInfoForm";
import ChangePasswordSection from "./components/ChangePasswordSection/ChangePasswordSection";
import DeleteAccountSection from "./components/DeleteAccountSection/DeleteAccountSection";
import ProfileAnalysisForm from "./components/ProfileAnalysis/ProfileAnalysisForm";
import localeText from "../../../../locale";

const BASE_TAB_IDS = ["general", "profile", "privacy"];
const PROFILE_ANALYSIS_TAB_ID = "profileAnalysis";
const GENERAL_ITEMS = [{ id: "regional", labelKey: "regionalPreferences", icon: "pi pi-globe" }];
const PROFILE_ITEMS = [
  { id: "profileSettings", labelKey: "settingsTabProfile", icon: "pi pi-user" },
  { id: "contactInfo", labelKey: "contactInfo", icon: "pi pi-id-card" },
];
const PRIVACY_ITEMS = [
  { id: "changePassword", labelKey: "changePassword", icon: "pi pi-lock" },
  { id: "deleteAccount", labelKey: "deleteAccount", icon: "pi pi-trash" },
];
const PROFILE_ANALYSIS_ITEMS = [
  { id: "analysis", labelKey: "profileAnalysisMenuItem", icon: "pi pi-chart-line" },
];

const getSidebarItems = (tabId) => {
  if (tabId === "general") return GENERAL_ITEMS;
  if (tabId === "profile") return PROFILE_ITEMS;
  if (tabId === "privacy") return PRIVACY_ITEMS;
  if (tabId === PROFILE_ANALYSIS_TAB_ID) return PROFILE_ANALYSIS_ITEMS;
  return [];
};

const getDefaultSubtab = (tabId) => {
  const items = getSidebarItems(tabId);
  return items.length ? items[0].id : null;
};

const getValidSubtab = (mainTab, sub) => {
  const ids = getSidebarItems(mainTab).map((i) => i.id);
  return ids.includes(sub) ? sub : getDefaultSubtab(mainTab);
};

const EditProfileInfo = (props) => {
  const { mainTab: paramTab, subtab: paramSub } = useParams();
  const history = useHistory();
  const location = useLocation();
  const didRedirect = useRef(false);
  const tabIds = useMemo(() => [...BASE_TAB_IDS, PROFILE_ANALYSIS_TAB_ID], []);
  const isValidMainTab = (t) => tabIds.includes(t);
  const mainTab = isValidMainTab(paramTab) ? paramTab : tabIds[0];
  const defaultSub = getDefaultSubtab(mainTab);
  const subtab = getValidSubtab(mainTab, paramSub) || defaultSub;

  const sidebarItems = useMemo(() => {
    const raw = getSidebarItems(mainTab);
    return raw.map((item) => ({
      ...item,
      label: localeText(item.labelKey) || item.labelKey,
    }));
  }, [mainTab]);

  const activeTabIndex = tabIds.indexOf(mainTab);

  const navigate = (toMain, toSub) => {
    const sub = toSub != null ? toSub : getDefaultSubtab(toMain);
    const path = `/dashboard-editprofile/${toMain}/${sub}`;
    history.push(path);
  };

  const handleTabChange = (e) => {
    const tabId = tabIds[e.index];
    if (tabId) navigate(tabId);
  };

  const handleSubtabSelect = (id) => {
    navigate(mainTab, id);
  };

  useEffect(() => {
    const path = location.pathname || "";
    const isBase = path === "/dashboard-editprofile" || path === "/dashboard-editprofile/";
    if (isBase && !didRedirect.current) {
      didRedirect.current = true;
      const main = tabIds[0];
      history.push(`/dashboard-editprofile/${main}/${getDefaultSubtab(main)}`);
    }
  }, [location.pathname, history, tabIds]);

  const renderContent = () => {
    if (mainTab === "general") {
      if (subtab === "regional") return <RegionalPreferences />;
      return null;
    }
    if (mainTab === "profile") {
      if (subtab === "profileSettings")
        return <ProfileSettingsForm user={props.user} setUserInfo={props.setUserInfo} />;
      if (subtab === "contactInfo")
        return <ContactInfoForm user={props.user} setUserInfo={props.setUserInfo} />;
      return null;
    }
    if (mainTab === "privacy") {
      if (subtab === "changePassword") return <ChangePasswordSection />;
      if (subtab === "deleteAccount") return <DeleteAccountSection />;
      return null;
    }
    if (mainTab === PROFILE_ANALYSIS_TAB_ID) {
      if (subtab === "analysis") return <ProfileAnalysisForm />;
      return null;
    }
    return null;
  };

  const panelContent =
    mainTab === PROFILE_ANALYSIS_TAB_ID ? (
      <div className="mt-0 pt-0">
        <div
          className="w-full"
          style={{
            maxWidth: "900px",
            marginLeft: "0",
            minWidth: "280px",
          }}
        >
          {renderContent()}
        </div>
      </div>
    ) : (
      <div className="flex flex-wrap mt-0 pt-0 gap-4">
        <div className="pr-7">
          <SettingsSidebar items={sidebarItems} activeId={subtab} onSelect={handleSubtabSelect} />
        </div>
        <div
          className="flex-1"
          style={{
            maxWidth: "400px",
            marginLeft: "0",
            minWidth: "280px",
          }}
        >
          {renderContent()}
        </div>
      </div>
    );

  return (
    <DashboardLayout>
      <div className="">
        <SettingsPageHeader plan={props.plan} currency={props.currency} />
        <TabView
          activeIndex={activeTabIndex}
          onTabChange={handleTabChange}
          className="settings-tabview mb-3"
        >
          <TabPanel header={localeText("settingsTabGeneral") || "Configurações gerais"}>
            {mainTab === "general" ? panelContent : null}
          </TabPanel>
          <TabPanel header={localeText("settingsTabProfile") || "Configurações de perfil"}>
            {mainTab === "profile" ? panelContent : null}
          </TabPanel>
          <TabPanel header={localeText("settingsTabPrivacy") || "Privacidade e segurança"}>
            {mainTab === "privacy" ? panelContent : null}
          </TabPanel>
          <TabPanel header={localeText("profileAnalysisTab") || "Análise de perfil"}>
            {mainTab === PROFILE_ANALYSIS_TAB_ID ? panelContent : null}
          </TabPanel>
        </TabView>
      </div>
    </DashboardLayout>
  );
};

export default EditProfileInfo;
